import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import GradingProvider from 'pages/Grading/PageProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import quizService from 'services/quiz';
import { Button, Loading, Stack, Text } from 'shell/components';
import { resolveFeatureFlags } from 'utils/helper';
import { mergeQuizSubmissionRefresh } from 'utils/quizGrading';

const PageProvider = ({ children }) => {
    const { department } = useApp();
    const { auth } = useAuth();
    const { id } = useParams();
    const { onMessage, setPingPayload } = useWebsocket();
    const { t } = useTranslation('quiz');
    const [quiz, setQuiz] = useState(null);
    const [submissions, setSubmissions] = useState(null);
    const [batch, setBatch] = useState([]);
    const [loadFailed, setLoadFailed] = useState(false);
    const scope = useRef(0);
    const submissionsRef = useRef(submissions);
    const refreshing = useRef(false);
    submissionsRef.current = submissions;

    const load = useCallback(async () => {
        const request = ++scope.current;
        setQuiz(null);
        setSubmissions(null);
        setBatch([]);
        setLoadFailed(false);
        refreshing.current = false;
        try {
            const [quiz, submissions] = await Promise.all([
                quizService.getQuiz(id),
                quizService.listQuizSubmissions(id),
            ]);
            if (request !== scope.current) return;
            if (!quiz || quiz.course_id !== auth.course_id || !Array.isArray(submissions)) {
                throw new Error('Invalid quiz grading data');
            }
            setQuiz(quiz);
            setSubmissions(submissions);
        } catch {
            if (request === scope.current) setLoadFailed(true);
        }
    }, [auth.course_id, id]);

    useEffect(() => {
        load();
        return () => {
            scope.current += 1;
        };
    }, [auth.owner_account_id, auth.role, load]);

    const pendingBatchIds = useMemo(
        () => [
            ...new Set([
                ...batch.map(({ id }) => id),
                ...(submissions ?? []).flatMap((submission) =>
                    (submission.feedback_regeneration?.active_batches ?? []).map(({ batch_id }) => batch_id)
                ),
            ]),
        ],
        [batch, submissions]
    );

    const refresh = useCallback(async () => {
        if (refreshing.current) return;
        refreshing.current = true;
        const request = scope.current;
        const beforeRefresh = new Map((submissionsRef.current ?? []).map((submission) => [submission.id, submission]));
        try {
            const refreshed = await quizService.listQuizSubmissions(id);
            if (request !== scope.current || !Array.isArray(refreshed)) return;
            setSubmissions((current) => mergeQuizSubmissionRefresh(current, refreshed, beforeRefresh));
            const activeIds = new Set(
                refreshed.flatMap((submission) =>
                    (submission.feedback_regeneration?.active_batches ?? []).map(({ batch_id }) => batch_id)
                )
            );
            setBatch((current) => current.filter(({ id }) => activeIds.has(id)));
        } catch {
            // Keep the current review and retry on the next progress message or poll.
        } finally {
            if (request === scope.current) refreshing.current = false;
        }
    }, [id]);

    useEffect(() => setPingPayload(() => ({ batch_ids: pendingBatchIds })), [pendingBatchIds, setPingPayload]);

    useEffect(
        () =>
            onMessage((event) => {
                if (event.type === 'pong' && pendingBatchIds.some((id) => event.batches?.[id]?.is_finished)) refresh();
            }),
        [onMessage, pendingBatchIds, refresh]
    );

    useEffect(() => {
        if (!pendingBatchIds.length) return;
        const interval = setInterval(refresh, 5000);
        return () => clearInterval(interval);
    }, [pendingBatchIds.length, refresh]);

    const gradingData = useMemo(
        () => ({
            currentAssignment: quiz ? resolveFeatureFlags({ ...quiz, type: 'quiz' }, department?.features) : null,
            submissions,
            setSubmissions,
            setBatch,
            resourcePath: '/quizzes',
            setInstructorHugoLiveContext: null,
        }),
        [department?.features, quiz, submissions]
    );

    if (loadFailed)
        return (
            <Stack alignItems={'center'} spacing={4} sx={{ p: 6 }}>
                <Text>{t('grading.load-error', 'Quiz submissions could not be loaded. Please try again.')}</Text>
                <Button onClick={load}>{t('common:controls.retry', 'Retry')}</Button>
            </Stack>
        );
    if (!quiz || quiz.id !== id || !submissions) return <Loading />;

    return <GradingProvider gradingData={gradingData}>{children}</GradingProvider>;
};

export default PageProvider;
